import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Text,
  ActionList,
  ActionMenu,
  Button,
  ButtonGroup,
} from "@primer/react";
import { SyncIcon, DownloadIcon, AlertIcon } from "@primer/octicons-react";
import { useCommonContext } from "../../../../contexts/common";
import { phongThiApi } from "../../../../api/phongThiApi";
import { NotifyHelper } from "../../../../helpers/toast";
import { userApi } from "../../../../api/userApi";
import { useCommonSelectedHook } from "../../../../hooks/useCommonSelectedHook";
import DataTable from "../../../../components/ui/data-table";
import { Dialog } from "@primer/react/drafts";
import MyButton from "../../../../components/ui/button/Button";
import { ex_phongthi } from "../../../../models/response/phong-thi/ex_phongthi";

interface GiaoVienData {
  id: number;
  text: string;
}

interface PhongThiData extends ex_phongthi {
  ten_phong: string;
  ten_loai_phong: string;
  so_thi_sinh: number;
}

interface GiaoVienCoiThiProps {
  kyThiKetThuc: boolean;
  dm_truong_id: number;
  ex_phongthi_id: number;
  ex_kythi_monthi_id: number;
}

const GiaoVienCoiThi: React.FC<GiaoVienCoiThiProps> = ({
  kyThiKetThuc,
  dm_truong_id,
  ex_phongthi_id,
  ex_kythi_monthi_id,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [phongHocs, setPhongHocs] = useState<PhongThiData[]>([]);
  const [giaoViens, setGiaoViens] = useState<GiaoVienData[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [changedData, setChangedData] = useState<PhongThiData[]>([]);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const { translate } = useCommonContext();

  const loadPhongHocs = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await phongThiApi.selectByMonThi(ex_kythi_monthi_id);
      if (res.is_success) {
        setPhongHocs(res.data);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
    setIsLoading(false);
  }, [ex_kythi_monthi_id]);

  const loadGiaoViens = useCallback(async () => {
    try {
      const res = await userApi.SelectTuVanVien(dm_coso_id);
      if (res.is_success) {
        setGiaoViens(
          res.data.map((x: any) => ({
            id: x.id,
            text: `${x.full_name} (${x.email})`,
          }))
        );
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
  }, [dm_coso_id]);

  useEffect(() => {
    loadPhongHocs();
    loadGiaoViens();
  }, [loadPhongHocs, loadGiaoViens]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await phongThiApi.updateGiaoVienCoiThi(changedData);
      if (res.is_success) {
        NotifyHelper.Success(translate("Success.Update"));
        loadPhongHocs();
        setShowUpdateDialog(false);
        setChangedData([]);
      } else {
        NotifyHelper.Error(res.message || translate("Error.UpdateFailed"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.Generic"));
    }
    setIsSaving(false);
  };

  const handleSelectionChange = (keys: number[]) => {
    setSelectedRows(keys);
  };

  return (
    <Box>
      <DataTable
        height="calc(100vh - 300px)"
        data={phongHocs}
        isLoading={isLoading}
        searchEnable
        paging={{ enable: true, pageSizeItems: [10, 20, 50, 200, 500] }}
        selection={
          !kyThiKetThuc
            ? {
                mode: "multiple",
                selectedRowKeys: selectedRows,
                onSelectionChanged: handleSelectionChange,
              }
            : undefined
        }
        actionComponent={
          <Box sx={{ display: "flex", gap: 2 }}>
            {kyThiKetThuc && (
              <Box
                sx={{
                  bg: "danger.emphasis",
                  color: "white",
                  px: 2,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Text sx={{ fontWeight: "bold" }}>
                  {translate("Kỳ thi đã kết thúc")}
                </Text>
              </Box>
            )}
            <MyButton
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              onClick={loadPhongHocs}
            />
          </Box>
        }
        columns={[
          {
            id: "index",
            caption: translate("TT"),
            width: "50px",
            align: "center",
            cellRender: (rowData: any) => (
              <Text>{phongHocs.indexOf(rowData) + 1}</Text>
            ),
          },
          {
            dataField: "ten_phong",
            caption: translate("Tên phòng"),
            width: "150px",
          },
          {
            dataField: "ten_loai_phong",
            caption: translate("Loại phòng"),
            width: "100px",
          },
          {
            dataField: "so_thi_sinh",
            caption: translate("Số thí sinh"),
            width: "100px",
          },
          {
            dataField: "userid_giamthi1",
            caption: translate("Giám thị 1"),
            width: "240px",
            cellRender: (data: PhongThiData) => {
              const giaoVien = giaoViens.find(
                (x) => x.id === data.userid_giamthi1
              );
              return <Text>{giaoVien ? giaoVien.text : ""}</Text>;
            },
          },
          {
            dataField: "userid_giamthi2",
            caption: translate("Giám thị 2"),
            width: "240px",
            cellRender: (data: PhongThiData) => {
              const giaoVien = giaoViens.find(
                (x) => x.id === data.userid_giamthi2
              );
              return <Text>{giaoVien ? giaoVien.text : ""}</Text>;
            },
          },
          {
            dataField: "userid_giamthi3",
            caption: translate("Giám thị 3"),
            width: "240px",
            cellRender: (data: PhongThiData) => {
              const giaoVien = giaoViens.find(
                (x) => x.id === data.userid_giamthi3
              );
              return <Text>{giaoVien ? giaoVien.text : ""}</Text>;
            },
          },
        ]}
      />

      {showUpdateDialog && (
        <Dialog
          title={translate("PhongThi.UpdateGiamThi")}
          onClose={() => setShowUpdateDialog(false)}
        >
          <Dialog.Body>{translate("PhongThi.ConfirmUpdate")}</Dialog.Body>
          <Dialog.Footer>
            <ButtonGroup>
              <Button onClick={() => setShowUpdateDialog(false)}>
                {translate("Base.Label.Cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {translate("Base.Label.Update")}
              </Button>
            </ButtonGroup>
          </Dialog.Footer>
        </Dialog>
      )}
    </Box>
  );
};

export default GiaoVienCoiThi;
